import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/site/SiteChrome";
import { Markdown } from "@/components/site/Markdown";
import { ShareRow } from "@/components/site/ShareRow";
import { AdSlots } from "@/components/site/AdSlot";
import { getArticle } from "@/lib/site";
import { useDocumentMeta, useSite } from "@/hooks/useSite";
import { currentLanguage, LANGUAGES, t } from "@/i18n";

const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLanguage() === "en" ? "en-GB" : "fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export default function Article() {
  const { slug } = useParams();
  const site = useSite();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    getArticle(slug)
      .then((data) => {
        setArticle(data);
        setState("ready");
      })
      .catch(() => setState("missing"));
  }, [slug]);

  useDocumentMeta({
    title: article
      ? `${article.title} — ${site?.brand?.name || t("app.name")}`
      : state === "missing"
        ? t("site.notFound")
        : t("site.loading"),
    description: article?.seo?.description || article?.excerpt,
    image: article?.cover_url,
    url: typeof window !== "undefined" ? window.location.href : "",
  });

  const url = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen">
      <div className="hero-wash">
        <SiteHeader brandName={site?.brand?.name} tagline={site?.brand?.tagline} />
      </div>

      <main className="mx-auto max-w-[720px] px-6 py-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-[color:var(--muted-ink)] hover:underline"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("site.backToJournal")}
        </Link>

        {state === "loading" ? (
          <p className="mt-10 flex items-center gap-2 text-[13.5px] text-[color:var(--muted-ink)]">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("site.loading")}
          </p>
        ) : state === "missing" ? (
          <p className="mt-10 text-[14px] text-[color:var(--ink-soft)]" data-testid="article-missing">
            {t("site.notFound")}
          </p>
        ) : (
          <article data-testid={`article-${article.slug}`}>
            <p className="font-num mt-8 text-[11px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
              {[formatDate(article.published_at), `${article.reading_minutes} ${t("site.minRead")}`]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <h1 className="mt-3 text-[34px] font-semibold leading-[1.12] tracking-tight sm:text-[42px]">
              {article.title}
            </h1>
            {article.excerpt ? (
              <p className="mt-4 text-[17px] leading-relaxed text-[color:var(--ink-soft)]">
                {article.excerpt}
              </p>
            ) : null}

            {article.video?.embed_url ? (
              <div className="surface mt-8 overflow-hidden rounded-xl">
                <div className="aspect-video w-full">
                  <iframe
                    src={article.video.embed_url}
                    title={article.title}
                    loading="lazy"
                    allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full border-0"
                    data-testid="article-video"
                  />
                </div>
              </div>
            ) : article.cover_url ? (
              <img
                src={article.cover_url}
                alt=""
                className="mt-8 w-full rounded-xl object-cover"
                loading="lazy"
              />
            ) : null}

            <AdSlots site={site} placement="article_inline" />

            <div className="mt-8 text-[16px] text-[color:var(--ink)]">
              <Markdown source={article.body_md} />
            </div>

            {article.tags?.length ? (
              <div className="mt-10 flex flex-wrap gap-1.5">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="rounded-full px-2.5 py-1 text-[11.5px]"
                    style={{ backgroundColor: "var(--paper-2)", color: "var(--ink-soft)" }}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            ) : null}

            <div className="mt-8 border-t pt-6" style={{ borderColor: "var(--line)" }}>
              <ShareRow url={url} title={article.title} />
            </div>

            {article.translations?.length ? (
              <p className="mt-6 text-[12.5px] text-[color:var(--muted-ink)]">
                {t("site.alsoIn")}{" "}
                {article.translations.map((sibling, index) => (
                  <React.Fragment key={sibling.slug}>
                    {index ? ", " : ""}
                    <Link to={`/blog/${sibling.slug}`} className="link-underline">
                      {LANGUAGES[sibling.lang] || sibling.lang}
                    </Link>
                  </React.Fragment>
                ))}
              </p>
            ) : null}

            <AdSlots site={site} placement="article_end" />
          </article>
        )}
      </main>

      <SiteFooter site={site} />
    </div>
  );
}
